/** @jsxImportSource @emotion/react */
import React from 'react';
import { Alert, Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import type { RotationCheckResult } from '../../../../../api/fieldsApi';

interface Crop {
  id: number | string;
  name: string;
  latinName?: string | null;
}

interface CropSectionProps {
  crops: Crop[];
  cropsLoading: boolean;
  rotationCheck: RotationCheckResult | null;
  onCropChange: (cropId: string | undefined) => void;
}

/** Crop selection dropdown + rotation check alert */
const CropSection: React.FC<CropSectionProps> = ({
  crops,
  cropsLoading,
  rotationCheck,
  onCropChange,
}) => {
  const { t } = useTranslation('plantings');
  return (
  <>
    <Form.Item
      label={t('add.crop')}
      name="cropId"
      rules={[{ required: true, message: t('add.cropRequired') }]}
    >
      <Select
        placeholder={t('add.cropRequired')}
        loading={cropsLoading}
        showSearch
        optionFilterProp="label"
        options={crops.map((crop) => ({
          value: crop.id,
          label: crop.latinName ? `${crop.name} (${crop.latinName})` : crop.name,
        }))}
        onChange={(cropId) => onCropChange(cropId ? String(cropId) : undefined)}
      />
    </Form.Item>

    {rotationCheck && rotationCheck.severity !== 'none' && (
      <Alert
        type={rotationCheck.severity === 'severe' ? 'error' : 'warning'}
        showIcon
        style={{ marginBottom: 16 }}
        message={
          rotationCheck.severity === 'severe'
            ? t('form.rotation.severe', { family: rotationCheck.familyRu })
            : t('form.rotation.possible', { family: rotationCheck.familyRu })
        }
        description={
          <>
            <div>{t('form.rotation.yieldLoss', { pct: rotationCheck.penaltyPct })}</div>
            {rotationCheck.reasons.map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: '#666' }}>
                • {r.label}
              </div>
            ))}
            {rotationCheck.recommendation && (
              <div style={{ marginTop: 4 }}>{rotationCheck.recommendation}</div>
            )}
          </>
        }
      />
    )}
  </>
  );
};

export default CropSection;
